import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

type ButtonVariant = 'primary' | 'secondary' | 'outline';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}

const getVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return `
        background: linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentSoft});
        color: white;
        border: none;
        box-shadow: ${theme.shadow.md};
        &:hover {
          transform: translateY(-2px);
          box-shadow: ${theme.shadow.lg};
          filter: brightness(1.02);
        }
        &:active {
          transform: translateY(0);
        }
      `;
    case 'secondary':
      return `
        background-color: ${theme.colors.accent};
        color: ${theme.colors.dark};
        border: none;
        box-shadow: ${theme.shadow.sm};
        &:hover {
          background-color: ${theme.colors.accentSoft};
          transform: translateY(-2px);
          box-shadow: ${theme.shadow.md};
        }
        &:active {
          transform: translateY(0);
        }
      `;
    case 'outline':
      return `
        background-color: transparent;
        border: 2px solid ${theme.colors.primary};
        color: ${theme.colors.primary};
        &:hover {
          background-color: ${theme.colors.primary};
          color: white;
          transform: translateY(-2px);
          box-shadow: ${theme.shadow.md};
        }
        &:active {
          transform: translateY(0);
        }
      `;
    default:
      return '';
  }
};

const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'small':
      return `
        padding: ${theme.spacing.sm} ${theme.spacing.md};
        font-size: 0.875rem;
        min-height: 40px;
      `;
    case 'medium':
      return `
        padding: ${theme.spacing.md} ${theme.spacing.lg};
        font-size: 1rem;
        min-height: 48px;
      `;
    case 'large':
      return `
        padding: ${theme.spacing.lg} ${theme.spacing.xl};
        font-size: 1.125rem;
        min-height: 56px;
      `;
    default:
      return '';
  }
};

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius};
  font-family: ${theme.fonts.primary};
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: none;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

  ${({ variant = 'primary' }) => getVariantStyles(variant as ButtonVariant)}
  ${({ size = 'medium' }) => getSizeStyles(size as ButtonSize)}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 82, 130, 0.3);
  }
`;

export const Button: React.FC<ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  children,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  ...props
}) => {
  return (
    <StyledButton variant={variant} size={size} fullWidth={fullWidth} {...props}>
      {children}
    </StyledButton>
  );
}; 